/**
 * A register: an employee's document file, or the company's.
 *
 * One component for both places it is shown — the employee's own page and the
 * compliance screen — because the office files from wherever it happens to be, and two
 * versions of the same list drift apart until one of them is wrong.
 *
 * Two kinds of thing, shown apart because they behave differently:
 *
 *  - Documents: one current copy each (ID card, medical fitness, DURC…). Uploading
 *    again renews it and archives the old one. A checklist, with one button per row.
 *  - Folders: as many files as you like (courses, nominations), with subfolders.
 *    Opened like a folder on a computer, with a drop zone inside.
 *
 * Both are document types on the server (migration 0022); `allowsMultiple` is the
 * difference. The office adds its own of either kind; for employees, a new one appears
 * in every employee's file.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { FileDrop } from '@/components/FileDrop';
import { Modal } from '@/components/Modal';
import { RegisterStatusBadge } from '@/components/register';
import { Switch } from '@/components/Switch';
import { Card, ErrorNotice, Field, Spinner } from '@/components/ui';
import { usePermissions } from '@/features/access/usePermissions';
import { DocumentViewer, type ViewableFile } from '@/features/documents/DocumentViewer';
import { RenameFileDialog } from '@/features/documents/RenameFileDialog';
import { useConfirm } from '@/components/ConfirmDialog';
import { useI18n } from '@/i18n';
import { api, ApiError, type DocumentType, type RegisterFile, type RegisterItem } from '@/lib/api';
import { dateShort, fileSize, isoDate } from '@/lib/format';

/** Matches the server's limit: a folder, a subfolder, and one more level. */
const MAX_DEPTH = 3;

type Scope = 'worker' | 'company' | 'vehicle';

/** What is being created or edited: a one-copy document, or a folder. */
type Editing =
  | { mode: 'create'; kind: 'document' | 'folder'; parent: RegisterItem | null }
  | { mode: 'edit'; item: RegisterItem };

interface EditForm {
  label: string;
  description: string;
  tracksExpiry: boolean;
  warnDaysBefore: number;
  isMandatory: boolean;
  shortCode: string;
}

const emptyRecord = () => ({
  issuedAt: isoDate(new Date()),
  expiresAt: '',
  referenceNumber: '',
  notes: '',
});

/** A freshly created type, shaped as the register item it will be once refetched. */
function itemFrom(type: DocumentType, depth: number): RegisterItem {
  return {
    code: type.code,
    label: type.label_it,
    description: type.description_it,
    tracksExpiry: type.tracks_expiry,
    isMandatory: type.is_mandatory,
    warnDaysBefore: type.warn_days_before,
    parentCode: type.parent_code,
    depth,
    allowsMultiple: type.allows_multiple,
    isCustom: true,
    shortCode: type.short_code,
    exempt: false,
    files: [],
    documentId: null,
    filename: null,
    issuedAt: null,
    expiresAt: null,
    referenceNumber: null,
    uploadedAt: null,
    daysLeft: null,
    status: 'not_provided',
  };
}

/** The holder part of an upload's details: the server copies the rest from the document. */
function holderOf(meta: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(meta).filter(([k]) => k === 'userId' || k === 'vehicleId'));
}

export function RegisterPanel({
  scope,
  userId,
  vehicleId,
}: {
  scope: Scope;
  /** Whose file, for scope "worker". */
  userId?: string;
  /** Which vehicle, for scope "vehicle". */
  vehicleId?: string;
}) {
  const { t } = useI18n();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const { can, role } = usePermissions();
  const canWrite = can('compliance', 'write');
  const canDeleteFiles = role === 'admin';

  const register = useQuery({
    // Under the same prefixes the pages use, so invalidating one refreshes both, but
    // never the same key: the pages cache the whole response, this caches the items.
    queryKey:
      scope === 'worker'
        ? ['worker-register', userId, 'panel']
        : scope === 'vehicle'
          ? ['vehicle-register', vehicleId, 'panel']
          : ['company-register', 'panel'],
    queryFn: async () =>
      scope === 'worker'
        ? (await api.workerRegister(userId!)).items
        : scope === 'vehicle'
          ? (await api.vehicleRegister(vehicleId!)).items
        : (await api.companyRegister()).items,
    enabled: scope === 'company' || Boolean(scope === 'vehicle' ? vehicleId : userId),
  });

  const refresh = async () => {
    await Promise.all(
      [
        ['worker-register'],
        ['company-register'],
        ['vehicle-register'],
        ['vehicles'],
        ['vehicle'],
        ['compliance'],
        ['alerts'],
        ['documents'],
      ].map(
        (queryKey) => queryClient.invalidateQueries({ queryKey }),
      ),
    );
  };

  const [openFolders, setOpenFolders] = useState<Set<string>>(new Set());
  const toggleFolder = (code: string, force?: boolean) =>
    setOpenFolders((prev) => {
      const next = new Set(prev);
      if (force ?? !next.has(code)) next.add(code);
      else next.delete(code);
      return next;
    });

  // --- Uploading ------------------------------------------------------------

  const [recording, setRecording] = useState<RegisterItem | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [form, setForm] = useState(emptyRecord);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  /** Set when adding a file to a document already on file (an ID card's back). */
  const [addingTo, setAddingTo] = useState<string | null>(null);

  const startUpload = (item: RegisterItem, picked: File[] = [], addTo: string | null = null) => {
    record.reset();
    setAddingTo(addTo);
    setFiles(picked);
    setForm({
      ...emptyRecord(),
      referenceNumber: item.allowsMultiple ? '' : (item.referenceNumber ?? ''),
    });
    setRecording(item);
  };

  const record = useMutation({
    mutationFn: async () => {
      const item = recording!;
      const meta = {
        typeCode: item.code,
        ...(scope === 'worker' ? { userId } : scope === 'vehicle' ? { vehicleId } : {}),
        ...(form.issuedAt ? { issuedAt: form.issuedAt } : {}),
        ...(form.expiresAt ? { expiresAt: form.expiresAt } : {}),
        ...(form.referenceNumber.trim() ? { referenceNumber: form.referenceNumber.trim() } : {}),
        ...(form.notes.trim() ? { notes: form.notes.trim() } : {}),
      };

      // No file: the dates alone, recorded before the scan arrives.
      if (files.length === 0) {
        await api.recordDocument({ ...meta, filename: `${item.label}.pdf` }, null);
        return;
      }

      // One at a time, so a failure names the file it failed on and the ones before
      // it are already safely filed — and dropped from the list, so "Save" again
      // retries only what is left.
      //
      // In a one-document item several files are ONE document (front and back): the
      // first is the document, the rest join it. In a folder each file is its own.
      setProgress({ done: 0, total: files.length });
      const remaining = [...files];
      let head = addingTo;
      for (const [index, file] of files.entries()) {
        try {
          const joins = !item.allowsMultiple && head ? { bundleWith: head } : {};
          const { documentId } = await api.recordDocument(
            { ...(head && !item.allowsMultiple ? { typeCode: meta.typeCode, ...holderOf(meta) } : meta), ...joins, filename: file.name },
            file,
          );
          if (!item.allowsMultiple && !head) head = documentId;
          // Saved progress survives a failure on the next file: "Save" again adds the
          // rest to the same document instead of starting a new one.
          if (!item.allowsMultiple) setAddingTo(head);
        } catch (error) {
          setFiles(remaining);
          const why = error instanceof ApiError ? error.message : t('documents.uploadFailed');
          throw new Error(t('folders.fileFailed', { name: file.name, reason: why }));
        }
        remaining.shift();
        setProgress({ done: index + 1, total: files.length });
      }
    },
    onSuccess: () => {
      if (recording?.allowsMultiple) toggleFolder(recording.code, true);
      setRecording(null);
      setFiles([]);
      setAddingTo(null);
    },
    onSettled: async () => {
      setProgress(null);
      await refresh();
    },
  });

  // --- Creating and editing -------------------------------------------------

  const [editing, setEditing] = useState<Editing | null>(null);
  const [editForm, setEditForm] = useState<EditForm>({
    label: '',
    description: '',
    tracksExpiry: true,
    warnDaysBefore: 30,
    isMandatory: false,
    shortCode: '',
  });

  const openCreate = (kind: 'document' | 'folder', parent: RegisterItem | null = null) => {
    save.reset();
    setEditForm({
      label: '',
      description: '',
      // A document added to the checklist is nearly always there because it has a
      // deadline. A folder is a place to keep things.
      tracksExpiry: kind === 'document',
      warnDaysBefore: 30,
      isMandatory: false,
      shortCode: '',
    });
    setEditing({ mode: 'create', kind, parent });
  };

  const openEdit = (item: RegisterItem) => {
    save.reset();
    setEditForm({
      label: item.label,
      description: item.description ?? '',
      tracksExpiry: item.tracksExpiry,
      warnDaysBefore: item.warnDaysBefore || 30,
      isMandatory: item.isMandatory,
      shortCode: item.shortCode ?? '',
    });
    setEditing({ mode: 'edit', item });
  };

  const editingKind: 'document' | 'folder' | null = !editing
    ? null
    : editing.mode === 'create'
      ? editing.kind
      : editing.item.allowsMultiple
        ? 'folder'
        : 'document';

  const save = useMutation({
    mutationFn: async () => {
      const isDocument = editingKind === 'document';
      const body = {
        label: editForm.label.trim(),
        description: editForm.description.trim() || null,
        // Empty: the files are named after the label.
        shortCode: editForm.shortCode.trim() || null,
        // Folders keep no deadline or requirement of their own: each file in one may
        // still carry an expiry date, entered when it is uploaded.
        ...(isDocument
          ? {
              tracksExpiry: editForm.tracksExpiry,
              warnDaysBefore: editForm.warnDaysBefore,
              isMandatory: editForm.isMandatory,
            }
          : {}),
      };
      if (editing!.mode === 'edit') {
        await api.updateFolder(editing!.item.code, body);
        return null;
      }
      const parent = editing!.parent;
      const { type } = await api.createFolder({
        ...body,
        description: body.description ?? undefined,
        shortCode: body.shortCode ?? undefined,
        allowsMultiple: !isDocument,
        scope,
        ...(parent ? { parentCode: parent.code } : {}),
      });
      return { type, depth: parent ? parent.depth + 1 : 0 };
    },
    onSuccess: async (created) => {
      setEditing(null);
      await refresh();
      if (!created) return;
      if (created.type.allows_multiple) {
        // A new folder opens, with its drop zone ready.
        toggleFolder(created.type.code, true);
        if (created.type.parent_code) toggleFolder(created.type.parent_code, true);
      } else {
        // A new document goes straight to its upload: "add the fire certificate" is
        // one job, not two.
        startUpload(itemFrom(created.type, created.depth));
      }
    },
  });

  const removeType = useMutation({
    mutationFn: (code: string) => api.deleteFolder(code),
    onSettled: refresh,
  });

  // --- Files ----------------------------------------------------------------

  const download = useMutation({
    mutationFn: async (id: string) => {
      // Audited server-side before the URL is issued.
      const { url } = await api.downloadUrl(id);
      window.open(url, '_blank', 'noopener');
    },
  });

  const removeFile = useMutation({
    mutationFn: (id: string) => api.deleteDocument(id),
    onSettled: refresh,
  });

  // --- Correcting a filed document -----------------------------------------

  /** The document whose dates or number are being corrected, and the folder it is in. */
  const [correcting, setCorrecting] = useState<{ file: RegisterFile; item: RegisterItem } | null>(
    null,
  );
  const [details, setDetails] = useState({ issuedAt: '', expiresAt: '', referenceNumber: '', notes: '' });

  const openCorrect = (file: RegisterFile, item: RegisterItem) => {
    correct.reset();
    setDetails({
      issuedAt: file.issuedAt ?? '',
      expiresAt: file.expiresAt ?? '',
      referenceNumber: file.referenceNumber ?? '',
      notes: file.notes ?? '',
    });
    setCorrecting({ file, item });
  };

  const correct = useMutation({
    mutationFn: () =>
      api.updateDocumentDetails(correcting!.file.id, {
        issuedAt: details.issuedAt || null,
        expiresAt: details.expiresAt || null,
        referenceNumber: details.referenceNumber.trim() || null,
        notes: details.notes.trim() || null,
      }),
    onSuccess: () => setCorrecting(null),
    onSettled: refresh,
  });

  /** The document whose file is being swapped (or attached, if it had none). */
  const [replacing, setReplacing] = useState<{ file: RegisterFile; item: RegisterItem } | null>(null);
  const [replacement, setReplacement] = useState<File | null>(null);

  const openReplace = (file: RegisterFile, item: RegisterItem) => {
    replace.reset();
    setReplacement(null);
    setReplacing({ file, item });
  };

  const replace = useMutation({
    mutationFn: () => api.replaceDocumentFile(replacing!.file.id, replacement!),
    onSuccess: () => setReplacing(null),
    onSettled: refresh,
  });

  /** The file being renamed. */
  const [renaming, setRenaming] = useState<RegisterFile | null>(null);

  // --- Quick look -----------------------------------------------------------

  /** The files being flipped through in the viewer, and which one is showing. */
  const [viewing, setViewing] = useState<{ files: ViewableFile[]; index: number } | null>(null);
  const view = (list: RegisterFile[], id: string) => {
    const files = list.filter((f) => f.hasFile);
    const index = files.findIndex((f) => f.id === id);
    if (index >= 0) setViewing({ files, index });
  };

  // --- Not required for this holder ------------------------------------------

  const exemption = useMutation({
    mutationFn: ({ code, on }: { code: string; on: boolean }) =>
      api.setExemption(on, {
        typeCode: code,
        ...(scope === 'vehicle' ? { vehicleId } : { userId }),
      }),
    onSettled: refresh,
  });

  // --- Removed entries ------------------------------------------------------

  const [showRemoved, setShowRemoved] = useState(false);
  const removed = useQuery({
    queryKey: [`${scope}-register`, 'removed', scope],
    queryFn: async () => (await api.removedFolders(scope)).types,
    enabled: canWrite,
  });
  const restore = useMutation({
    mutationFn: (code: string) => api.restoreFolder(code),
    onSettled: refresh,
  });

  if (register.isLoading) return <Spinner />;
  if (register.error) return <ErrorNotice error={register.error} />;
  const items = register.data ?? [];

  const childrenOf = (code: string | null) => items.filter((i) => i.parentCode === code);
  const known = new Set(items.map((i) => i.code));
  // Top level: no parent, or a parent that is no longer there.
  const top = items.filter((i) => !i.parentCode || !known.has(i.parentCode));
  const documents = top.filter((i) => !i.allowsMultiple);
  const folders = top.filter((i) => i.allowsMultiple);

  // Excused documents are not required of this holder, so not counted.
  const required = documents.filter((i) => i.isMandatory && i.status !== 'exempt');
  const present = required.filter((i) => i.documentId).length;
  const expired = documents.filter((i) => i.status === 'expired').length;
  const expiring = documents.filter((i) => i.status === 'expiring').length;

  const actionError =
    removeType.error ?? removeFile.error ?? download.error ?? restore.error ?? exemption.error;

  const confirmDeleteType = async (item: RegisterItem) => {
    const key = item.allowsMultiple ? 'folders.deleteFolderConfirm' : 'folders.deleteDocumentConfirm';
    if (await confirm({ message: t(key, { name: item.label }), confirmLabel: t('common.delete'), danger: true })) {
      removeType.mutate(item.code);
    }
  };

  /** What can be done to one filed document. */
  const fileMenu = (f: RegisterFile, item: RegisterItem): MenuItem[] => [
    ...(canWrite
      ? [
          ...(f.hasFile ? [{ label: t('rename.file'), onSelect: () => setRenaming(f) }] : []),
          { label: t('folders.correct'), onSelect: () => openCorrect(f, item) },
          {
            label: f.hasFile ? t('folders.replaceFile') : t('folders.attachFile'),
            onSelect: () => openReplace(f, item),
          },
        ]
      : []),
    ...(canDeleteFiles
      ? [
          {
            label: t('common.delete'),
            danger: true,
            onSelect: async () => {
              if (await confirm({ message: t('documents.deleteConfirm', { name: f.filename }), confirmLabel: t('common.delete'), danger: true })) {
                removeFile.mutate(f.id);
              }
            },
          },
        ]
      : []),
  ];

  // --- Rendering ------------------------------------------------------------

  const documentRow = (item: RegisterItem) => {
    // The document on file: its first file, and how many files it has (front, back).
    const current = item.files.find((f) => f.id === item.documentId) ?? item.files[0];
    const sides = current ? item.files.filter((f) => (f.bundleId ?? f.id) === current.id).length : 0;
    const menu: MenuItem[] = [
      // Also on the row as an icon; in the menu too because on a phone the icon is
      // hidden to give the document's name the room.
      ...(current?.hasFile
        ? [
            { label: t('viewer.preview'), onSelect: () => view(item.files, current.id) },
            { label: t('common.download'), onSelect: () => download.mutate(current.id) },
          ]
        : []),
      ...(item.files.length > 0
        ? [
            {
              label: openFolders.has(item.code) ? t('folders.hideFiles') : t('folders.showFiles', { count: item.files.length }),
              onSelect: () => toggleFolder(item.code),
            },
          ]
        : []),
      // The document on file first: correcting it is the common case.
      ...(canWrite && current?.hasFile && !item.allowsMultiple
        ? [{ label: t('folders.addFile'), onSelect: () => startUpload(item, [], current.id) }]
        : []),
      ...(canWrite && current
        ? [
            ...(current.hasFile ? [{ label: t('rename.file'), onSelect: () => setRenaming(current) }] : []),
            { label: t('folders.correct'), onSelect: () => openCorrect(current, item) },
            {
              label: current.hasFile ? t('folders.replaceFile') : t('folders.attachFile'),
              onSelect: () => openReplace(current, item),
            },
          ]
        : []),
      // Required of everyone, but not of this one: only while nothing is on file.
      ...(canWrite && scope !== 'company' && item.isMandatory && !item.documentId
        ? [
            {
              label: item.exempt
                ? t('exempt.requiredAgain')
                : scope === 'vehicle'
                  ? t('exempt.notRequiredVehicle')
                  : t('exempt.notRequiredPerson'),
              separated: true,
              onSelect: async () => {
                if (item.exempt || (await confirm({ message: t('exempt.confirm', { name: item.label }) }))) {
                  exemption.mutate({ code: item.code, on: !item.exempt });
                }
              },
            },
          ]
        : []),
      // Then the entry itself — standard ones included.
      ...(canWrite
        ? [
            { label: t('folders.editItem'), onSelect: () => openEdit(item), separated: true },
            { label: t('folders.removeItem'), danger: true, onSelect: () => confirmDeleteType(item) },
          ]
        : []),
    ];
    const showHistory = openFolders.has(item.code) && item.files.length > 0;

    return (
      <li key={item.code} className="border-b border-border last:border-0">
        <div className="flex items-center gap-3 px-4 py-3">
          <StatusDot status={item.status} />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold">
              {item.label}
              {item.isMandatory ? (
                <span className="text-danger ms-1" title={t('compliance.mandatory')}>
                  *
                </span>
              ) : null}
            </div>
            <div className="text-xs text-muted truncate">
              {current ? (
                <>
                  {current.hasFile ? (
                    <button
                      type="button"
                      className="hover:text-primary hover:underline"
                      title={t('viewer.preview')}
                      onClick={() => view(item.files, current.id)}
                    >
                      {current.filename}
                    </button>
                  ) : (
                    t('folders.noScan')
                  )}
                  {sides > 1 ? ` · ${t('folders.fileCount', { count: sides })}` : ''}
                  {current.expiresAt
                    ? ` · ${t('employees.expiresOn', { date: dateShort(current.expiresAt) })}`
                    : ''}
                </>
              ) : (
                (item.description ?? t('folders.notOnFile'))
              )}
            </div>
          </div>

          <div className="max-sm:hidden">
            {item.status !== 'not_provided' ? <RegisterStatusBadge item={item} /> : null}
          </div>

          <div className="max-sm:hidden flex">
            {current?.hasFile ? (
              <>
                <IconButton label={t('viewer.preview')} onClick={() => view(item.files, current.id)}>
                  <EyeIcon />
                </IconButton>
                <IconButton label={t('common.download')} onClick={() => download.mutate(current.id)}>
                  <DownloadIcon />
                </IconButton>
              </>
            ) : (
              <span className="block w-16" aria-hidden />
            )}
          </div>

          {canWrite && current && !current.hasFile ? (
            // The details are on file (typed in, or from Excel) but the scan is not: attach
            // it to them, keeping their dates, rather than recording the document again.
            <button
              type="button"
              className="btn-primary h-8 sm:min-w-[96px]"
              onClick={() => openReplace(current, item)}
            >
              {t('folders.attachFile')}
            </button>
          ) : canWrite ? (
            <button
              type="button"
              // Red only where something is wrong: a required document missing or out of
              // date. An optional one not on file is not a problem to be shouted about.
              className={`${
                item.status === 'missing' || item.status === 'expired' ? 'btn-primary' : 'btn-secondary'
              } h-8 sm:min-w-[96px]`}
              onClick={() => startUpload(item)}
            >
              {item.documentId ? t('compliance.renew') : t('folders.upload')}
            </button>
          ) : null}

          <RowMenu label={t('folders.more')} items={menu} />
        </div>

        {showHistory ? (
          <ul className="bg-surface-alt border-t border-border px-4 py-1 ps-10">
            {item.files.map((f) => (
              <FileLine
                key={f.id}
                file={f}
                onView={() => view(item.files, f.id)}
                onDownload={() => download.mutate(f.id)}
                menu={fileMenu(f, item)}
              />
            ))}
          </ul>
        ) : null}
      </li>
    );
  };

  const folderRow = (item: RegisterItem): ReactNode => {
    const open = openFolders.has(item.code);
    const subs = childrenOf(item.code);
    const count = item.files.length;
    const menu: MenuItem[] = canWrite
      ? [
          ...(item.depth + 1 < MAX_DEPTH
            ? [{ label: t('folders.newSubfolder'), onSelect: () => openCreate('folder', item) }]
            : []),
          { label: t('folders.rename'), onSelect: () => openEdit(item) },
          { label: t('common.delete'), danger: true, onSelect: () => confirmDeleteType(item) },
        ]
      : [];

    return (
      <li key={item.code} className="border-b border-border last:border-0">
        <div className="flex items-center gap-3 px-4 py-3 hover:bg-surface-alt">
          <button
            type="button"
            className="flex items-center gap-3 min-w-0 flex-1 text-start"
            onClick={() => toggleFolder(item.code)}
            aria-expanded={open}
          >
            <Chevron open={open} />
            <FolderIcon open={open} />
            <span className="min-w-0">
              <span className="block text-sm font-semibold truncate">{item.label}</span>
              <span className="block text-xs text-muted truncate">
                {[
                  count === 0 && subs.length === 0
                    ? t('folders.emptyShort')
                    : count > 0
                      ? t(count === 1 ? 'folders.countOne' : 'folders.countMany', { count })
                      : null,
                  subs.length > 0
                    ? t(subs.length === 1 ? 'folders.subCountOne' : 'folders.subCountMany', {
                        count: subs.length,
                      })
                    : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            </span>
          </button>

          {canWrite ? (
            <button
              type="button"
              className="btn-secondary h-8 sm:min-w-[96px]"
              onClick={() => startUpload(item)}
            >
              {t('folders.upload')}
            </button>
          ) : null}
          <RowMenu label={t('folders.more')} items={menu} />
        </div>

        {open ? (
          <div className="bg-surface-alt border-t border-border ps-6 sm:ps-10 pe-4 py-3 space-y-3">
            {subs.length > 0 ? (
              <ul className="card overflow-visible">
                {subs.map((s) => (s.allowsMultiple ? folderRow(s) : documentRow(s)))}
              </ul>
            ) : null}

            {count > 0 ? (
              <ul className="card px-3 py-1">
                {item.files.map((f) => (
                  <FileLine
                    key={f.id}
                    file={f}
                    onView={() => view(item.files, f.id)}
                    onDownload={() => download.mutate(f.id)}
                    menu={fileMenu(f, item)}
                  />
                ))}
              </ul>
            ) : null}

            {canWrite ? (
              <FileDrop
                onPick={(file) => startUpload(item, [file])}
                onPickMany={(picked) => startUpload(item, picked)}
                label={t('folders.dropInto', { name: item.label })}
              />
            ) : count === 0 && subs.length === 0 ? (
              <p className="text-sm text-muted">{t('folders.emptyFolder')}</p>
            ) : null}
          </div>
        ) : null}
      </li>
    );
  };

  return (
    <div className="space-y-5">
      {actionError ? <InlineError error={actionError} /> : null}

      <RenameFileDialog
        filename={renaming?.filename ?? null}
        onClose={() => setRenaming(null)}
        onSave={async (filename) => {
          await api.updateDocumentDetails(renaming!.id, { filename });
          await refresh();
        }}
      />

      <DocumentViewer
        files={viewing?.files ?? []}
        index={viewing?.index ?? null}
        onIndex={(index) => setViewing((v) => (v ? { ...v, index } : v))}
        onClose={() => setViewing(null)}
        onDownload={(id) => download.mutate(id)}
      />

      {/* --- Documents: the checklist ------------------------------------- */}
      <section className="space-y-2">
        <SectionHeader
          title={t('folders.documentsTitle')}
          hint={
            required.length > 0
              ? t('folders.summary', { present, required: required.length })
              : t('folders.documentsHint')
          }
          extra={
            <>
              {expired > 0 ? (
                <span className="font-semibold text-danger">
                  {t('folders.expiredCount', { count: expired })}
                </span>
              ) : null}
              {expiring > 0 ? (
                <span className="font-semibold text-warning">
                  {t('folders.expiringCount', { count: expiring })}
                </span>
              ) : null}
            </>
          }
          action={
            canWrite ? (
              <button type="button" className="btn-secondary h-8" onClick={() => openCreate('document')}>
                <PlusIcon />
                {scope === 'company' ? t('folders.addCompanyDocument') : t('folders.newDocument')}
              </button>
            ) : null
          }
        />
        <Card padded={false}>
          <ul>{documents.map(documentRow)}</ul>
          {documents.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-muted">{t('folders.noDocuments')}</div>
          ) : null}
        </Card>
      </section>

      {/* --- Folders -------------------------------------------------------- */}
      <section className="space-y-2">
        <SectionHeader
          title={t('folders.foldersTitle')}
          hint={
            scope === 'company'
              ? t('folders.foldersHintCompany')
              : scope === 'vehicle'
                ? t('folders.foldersHintVehicle')
                : t('folders.foldersHint')
          }
          action={
            canWrite ? (
              <button type="button" className="btn-secondary h-8" onClick={() => openCreate('folder')}>
                <PlusIcon />
                {t('folders.newFolder')}
              </button>
            ) : null
          }
        />
        <Card padded={false}>
          <ul>{folders.map(folderRow)}</ul>
          {folders.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-muted">{t('folders.noFolders')}</div>
          ) : null}
        </Card>
      </section>

      {/* --- Removed entries: quiet, but never lost -------------------------- */}
      {canWrite && (removed.data?.length ?? 0) > 0 ? (
        <section className="space-y-2">
          <button
            type="button"
            className="text-xs text-muted hover:text-primary hover:underline"
            aria-expanded={showRemoved}
            onClick={() => setShowRemoved(!showRemoved)}
          >
            {t('folders.removedTitle', { count: removed.data!.length })}
          </button>
          {showRemoved ? (
            <Card padded={false}>
              <ul>
                {removed.data!.map((type) => (
                  <li
                    key={type.code}
                    className="flex items-center gap-3 px-4 py-2.5 border-b border-border last:border-0"
                  >
                    {type.allows_multiple ? <FolderIcon open={false} /> : <FileIcon />}
                    <span className="text-sm text-muted flex-1 min-w-0 truncate">{type.label_it}</span>
                    <button
                      type="button"
                      className="btn-secondary h-8"
                      disabled={restore.isPending}
                      onClick={() => restore.mutate(type.code)}
                    >
                      {t('folders.restore')}
                    </button>
                  </li>
                ))}
              </ul>
            </Card>
          ) : null}
        </section>
      ) : null}

      {/* --- Correct a filed document ---------------------------------------- */}
      <Modal
        open={correcting !== null}
        onClose={() => setCorrecting(null)}
        size="sm"
        title={correcting ? t('folders.correctTitle', { name: correcting.item.label }) : ''}
        description={t('folders.correctHint')}
        footer={
          <>
            <button
              type="button"
              className="btn-primary"
              disabled={(correcting?.item.tracksExpiry && !details.expiresAt) || correct.isPending}
              onClick={() => correct.mutate()}
            >
              {correct.isPending ? t('common.saving') : t('common.save')}
            </button>
            <button type="button" className="btn-secondary" onClick={() => setCorrecting(null)}>
              {t('common.cancel')}
            </button>
          </>
        }
      >
        {correcting ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm text-muted">
              <FileIcon />
              <span className="truncate">
                {correcting.file.hasFile ? correcting.file.filename : t('folders.noScan')}
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field
                label={`${t('compliance.expiry')}${correcting.item.tracksExpiry ? ' *' : ''}`}
                hint={correcting.item.tracksExpiry ? t('compliance.expiryRequired') : t('common.optional')}
              >
                <input
                  type="date"
                  className="input"
                  value={details.expiresAt}
                  onChange={(e) => setDetails({ ...details, expiresAt: e.target.value })}
                />
              </Field>
              <Field label={t('compliance.issuedAt')}>
                <input
                  type="date"
                  className="input"
                  value={details.issuedAt}
                  onChange={(e) => setDetails({ ...details, issuedAt: e.target.value })}
                />
              </Field>
              <Field label={t('compliance.referenceNumber')} hint={t('common.optional')}>
                <input
                  className="input"
                  value={details.referenceNumber}
                  onChange={(e) => setDetails({ ...details, referenceNumber: e.target.value })}
                />
              </Field>
              <Field label={t('folders.notes')} hint={t('common.optional')}>
                <input
                  className="input"
                  maxLength={2000}
                  value={details.notes}
                  onChange={(e) => setDetails({ ...details, notes: e.target.value })}
                />
              </Field>
            </div>
            {correct.error ? <InlineError error={correct.error} /> : null}
          </div>
        ) : null}
      </Modal>

      {/* --- Replace (or attach) a filed document's file --------------------- */}
      <Modal
        open={replacing !== null}
        onClose={() => (replace.isPending ? undefined : setReplacing(null))}
        size="sm"
        title={
          replacing
            ? t(replacing.file.hasFile ? 'folders.replaceTitle' : 'folders.attachTitle', {
                name: replacing.item.label,
              })
            : ''
        }
        description={t('folders.replaceHint')}
        footer={
          <>
            <button
              type="button"
              className="btn-primary"
              disabled={!replacement || replace.isPending}
              onClick={() => replace.mutate()}
            >
              {replace.isPending ? t('documents.uploading') : t('common.save')}
            </button>
            <button
              type="button"
              className="btn-secondary"
              disabled={replace.isPending}
              onClick={() => setReplacing(null)}
            >
              {t('common.cancel')}
            </button>
          </>
        }
      >
        {replacing ? (
          <div className="space-y-3">
            {replacing.file.hasFile ? (
              <div className="flex items-center gap-2 text-sm text-muted">
                <FileIcon />
                <span className="truncate line-through">{replacing.file.filename}</span>
              </div>
            ) : null}
            {replacement ? (
              <div className="flex items-center gap-3 px-3 py-2 rounded border border-border bg-surface-alt">
                <FileIcon />
                <span className="text-sm font-medium truncate flex-1">{replacement.name}</span>
                <span className="text-xs text-subtle tabular whitespace-nowrap">
                  {fileSize(replacement.size)}
                </span>
                <button
                  type="button"
                  className="td-action-danger"
                  disabled={replace.isPending}
                  onClick={() => setReplacement(null)}
                >
                  {t('common.remove')}
                </button>
              </div>
            ) : (
              <FileDrop onPick={setReplacement} label={t('folders.dropOne')} />
            )}
            {replace.error ? <InlineError error={replace.error} /> : null}
          </div>
        ) : null}
      </Modal>

      {/* --- Upload dialog ------------------------------------------------- */}
      <Modal
        open={recording !== null}
        onClose={() => (record.isPending ? undefined : setRecording(null))}
        title={
          recording
            ? t(addingTo ? 'folders.addFileTitle' : 'folders.uploadTitle', { label: recording.label })
            : ''
        }
        description={
          [
            addingTo
              ? t('folders.addFileHint')
              : recording?.allowsMultiple
                ? t('folders.multipleExplainer')
                : recording?.documentId
                  ? t('folders.renewExplainer')
                  : null,
            // Nobody renames "scan_0042.pdf" by hand any more.
            t('folders.autoNameHint'),
          ]
            .filter(Boolean)
            .join(' ')
        }
        footer={
          <>
            <button
              type="button"
              className="btn-primary"
              disabled={
                (!addingTo && recording?.tracksExpiry && !form.expiresAt) ||
                ((recording?.allowsMultiple || addingTo) && files.length === 0) ||
                record.isPending
              }
              onClick={() => record.mutate()}
            >
              {record.isPending
                ? progress
                  ? t('folders.uploadingProgress', { done: progress.done, total: progress.total })
                  : t('common.saving')
                : files.length > 1
                  ? t('folders.uploadN', { count: files.length })
                  : t('common.save')}
            </button>
            <button
              type="button"
              className="btn-secondary"
              disabled={record.isPending}
              onClick={() => setRecording(null)}
            >
              {t('common.cancel')}
            </button>
          </>
        }
      >
        {recording ? (
          <div className="space-y-4">
            <div className="space-y-2">
              {files.length > 0 ? (
                <ul className="space-y-1.5">
                  {files.map((file, index) => (
                    <li
                      key={`${file.name}-${index}`}
                      className="flex items-center gap-3 px-3 py-2 rounded border border-border bg-surface-alt"
                    >
                      <FileIcon />
                      <span className="text-sm font-medium truncate flex-1">{file.name}</span>
                      <span className="text-xs text-subtle tabular whitespace-nowrap">
                        {fileSize(file.size)}
                      </span>
                      <button
                        type="button"
                        className="td-action-danger"
                        disabled={record.isPending}
                        onClick={() => setFiles(files.filter((_, i) => i !== index))}
                      >
                        {t('common.remove')}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}

              {recording.allowsMultiple ? (
                <FileDrop
                  onPick={(file) => setFiles([...files, file])}
                  onPickMany={(picked) => setFiles([...files, ...picked])}
                  busy={record.isPending}
                  label={files.length > 0 ? t('folders.addMore') : t('folders.dropMany')}
                />
              ) : (
                <>
                  <FileDrop
                    onPick={(file) => setFiles([...files, file])}
                    onPickMany={(picked) => setFiles([...files, ...picked])}
                    busy={record.isPending}
                    label={files.length > 0 ? t('folders.addAnotherSide') : t('folders.dropSides')}
                  />
                  {files.length > 1 && !addingTo ? (
                    <p className="text-xs text-muted">{t('folders.sidesTogether', { count: files.length })}</p>
                  ) : null}
                </>
              )}
            </div>

            {addingTo ? null : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field
                  label={`${t('compliance.expiry')}${recording.tracksExpiry ? ' *' : ''}`}
                  hint={recording.tracksExpiry ? t('compliance.expiryRequired') : t('common.optional')}
                >
                  <input
                    type="date"
                    className="input"
                    value={form.expiresAt}
                    onChange={(e) => setForm({ ...form, expiresAt: e.target.value })}
                  />
                </Field>
                <Field label={t('compliance.issuedAt')}>
                  <input
                    type="date"
                    className="input"
                    value={form.issuedAt}
                    onChange={(e) => setForm({ ...form, issuedAt: e.target.value })}
                  />
                </Field>
                <Field label={t('compliance.referenceNumber')} hint={t('common.optional')}>
                  <input
                    className="input"
                    value={form.referenceNumber}
                    onChange={(e) => setForm({ ...form, referenceNumber: e.target.value })}
                  />
                </Field>
                <Field label={t('folders.notes')} hint={t('common.optional')}>
                  <input
                    className="input"
                    maxLength={2000}
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  />
                </Field>
              </div>
            )}

            {!addingTo && !recording.allowsMultiple && files.length === 0 ? (
              <p className="text-xs text-muted">{t('folders.noFileHint')}</p>
            ) : null}

            {record.error ? <InlineError error={record.error} /> : null}
          </div>
        ) : null}
      </Modal>

      {/* --- Create / edit dialog ------------------------------------------ */}
      <Modal
        open={editing !== null}
        onClose={() => setEditing(null)}
        size="sm"
        title={
          !editing
            ? ''
            : editing.mode === 'edit'
              ? t('folders.editTitle', { name: editing.item.label })
              : editing.kind === 'folder'
                ? editing.parent
                  ? t('folders.subfolderTitle', { name: editing.parent.label })
                  : t('folders.newFolder')
                : scope === 'company'
                  ? t('folders.addCompanyDocument')
                  : t('folders.newDocument')
        }
        description={
          editing?.mode === 'create'
            ? editing.kind === 'folder'
              ? scope === 'worker'
                ? t('folders.newFolderHintWorker')
                : scope === 'vehicle'
                  ? t('folders.newFolderHintVehicle')
                  : t('folders.newFolderHintCompany')
              : scope === 'worker'
                ? t('folders.newDocumentHintWorker')
                : scope === 'vehicle'
                  ? t('folders.newDocumentHintVehicle')
                  : t('folders.newDocumentHintCompany')
            : undefined
        }
        footer={
          <>
            <button
              type="button"
              className="btn-primary"
              disabled={!editForm.label.trim() || save.isPending}
              onClick={() => save.mutate()}
            >
              {save.isPending
                ? t('common.saving')
                : editing?.mode === 'create'
                  ? editingKind === 'document'
                    ? t('folders.createAndUpload')
                    : t('folders.createFolder')
                  : t('common.save')}
            </button>
            <button type="button" className="btn-secondary" onClick={() => setEditing(null)}>
              {t('common.cancel')}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <Field label={t('folders.name')}>
            <input
              className="input"
              autoFocus
              maxLength={80}
              value={editForm.label}
              placeholder={
                editingKind === 'folder'
                  ? t('folders.folderPlaceholder')
                  : scope === 'company'
                    ? t('folders.companyPlaceholder')
                    : scope === 'vehicle'
                      ? t('folders.vehiclePlaceholder')
                      : t('folders.workerPlaceholder')
              }
              onChange={(e) => setEditForm({ ...editForm, label: e.target.value })}
            />
          </Field>
          <Field label={t('folders.description')} hint={t('common.optional')}>
            <input
              className="input"
              maxLength={300}
              value={editForm.description}
              onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
            />
          </Field>
          <Field
            label={t('folders.shortCode')}
            hint={t('folders.shortCodeHint', {
              example: autoNameExample(editForm.shortCode.trim() || editForm.label.trim() || 'C.I.', scope),
            })}
          >
            <input
              className="input w-48"
              maxLength={40}
              value={editForm.shortCode}
              placeholder={editForm.label.trim() || 'C.I.'}
              onChange={(e) => setEditForm({ ...editForm, shortCode: e.target.value })}
            />
          </Field>

          {editingKind === 'document' ? (
            <>
              <ToggleRow
                checked={editForm.tracksExpiry}
                onChange={(v) => setEditForm({ ...editForm, tracksExpiry: v })}
                label={t('folders.tracksExpiry')}
                hint={t('folders.tracksExpiryHint')}
              />
              {editForm.tracksExpiry ? (
                <Field label={t('folders.warnDays')}>
                  <input
                    type="number"
                    className="input w-28"
                    min={1}
                    max={365}
                    value={editForm.warnDaysBefore}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        warnDaysBefore: Math.min(365, Math.max(1, Number(e.target.value) || 1)),
                      })
                    }
                  />
                </Field>
              ) : null}
              <ToggleRow
                checked={editForm.isMandatory}
                onChange={(v) => setEditForm({ ...editForm, isMandatory: v })}
                label={t('folders.mandatory')}
                hint={
                  scope === 'worker'
                    ? t('folders.mandatoryWorkerHint')
                    : scope === 'vehicle'
                      ? t('folders.mandatoryVehicleHint')
                      : t('folders.mandatoryCompanyHint')
                }
              />
            </>
          ) : null}

          {save.error ? <InlineError error={save.error} /> : null}
        </div>
      </Modal>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pieces
// ---------------------------------------------------------------------------

/**
 * What a file of this type will be called, for the dialog's hint. The server's rule
 * (domain/filenames.ts), shortened: accents and spaces out, then whose and the expiry.
 */
function autoNameExample(code: string, scope: 'worker' | 'company' | 'vehicle'): string {
  const part = code
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/['’`]/g, '')
    .replace(/[^A-Za-z0-9.-]+/g, '_')
    .replace(/^_+|_+$/g, '');
  const holder = scope === 'worker' ? '_Mario_Rossi' : scope === 'vehicle' ? '_GX482TB' : '';
  return `${part || 'Documento'}${holder}_scadenza_31-12-2027.pdf`;
}

function SectionHeader({
  title,
  hint,
  extra,
  action,
}: {
  title: string;
  hint?: string;
  extra?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-end gap-x-3 gap-y-2">
      <div className="min-w-0 flex-1">
        <h3 className="text-sm font-bold">{title}</h3>
        <div className="flex flex-wrap items-center gap-x-3 text-xs text-muted">
          {hint ? <span>{hint}</span> : null}
          {extra}
        </div>
      </div>
      {action}
    </div>
  );
}

function FileLine({
  file,
  badge,
  onView,
  onDownload,
  menu,
}: {
  file: RegisterFile;
  badge?: string;
  onView: () => void;
  onDownload: () => void;
  menu: MenuItem[];
}) {
  const { t } = useI18n();
  const expired = file.daysLeft !== null && file.daysLeft < 0;
  const details = [
    file.issuedAt ? `${t('compliance.issued')} ${dateShort(file.issuedAt)}` : null,
    file.referenceNumber,
    file.hasFile && file.sizeBytes > 0 ? fileSize(file.sizeBytes) : null,
    file.uploadedByName,
    file.notes,
  ].filter(Boolean);

  return (
    <li className="flex items-center gap-3 py-2 border-t border-border first:border-0">
      <FileIcon />
      <div className="min-w-0 flex-1">
        <div className="text-sm truncate" title={file.filename}>
          {file.hasFile ? (
            <button type="button" className="hover:text-primary hover:underline text-start" onClick={onView}>
              {file.filename}
            </button>
          ) : (
            <span className="italic text-muted">{t('folders.noScan')}</span>
          )}
          {badge ? (
            <span className="ms-2 text-[11px] uppercase tracking-wide text-subtle">{badge}</span>
          ) : null}
        </div>
        <div className="text-xs text-muted truncate">
          {file.expiresAt ? (
            <span className={expired ? 'text-danger font-semibold' : undefined}>
              {t('employees.expiresOn', { date: dateShort(file.expiresAt) })}
              {details.length > 0 ? ' · ' : ''}
            </span>
          ) : null}
          {details.join(' · ')}
        </div>
      </div>
      {file.hasFile ? (
        <>
          <IconButton label={t('viewer.preview')} onClick={onView}>
            <EyeIcon />
          </IconButton>
          <IconButton label={t('common.download')} onClick={onDownload}>
            <DownloadIcon />
          </IconButton>
        </>
      ) : null}
      <RowMenu label={t('folders.more')} items={menu} />
    </li>
  );
}

interface MenuItem {
  label: string;
  onSelect: () => void;
  danger?: boolean;
  /** A thin line above: separates "this file" from "this entry in the list". */
  separated?: boolean;
}

/**
 * The row's less common actions, behind "⋯".
 *
 * Every row used to show all of them as links side by side — Rinnova, Mostra,
 * Sottocartella, Modifica, Elimina — and a list of sixteen such rows was a wall of
 * identical words in which the one action you wanted had to be read for.
 */
function RowMenu({ label, items }: { label: string; items: MenuItem[] }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent | KeyboardEvent) => {
      if (e instanceof KeyboardEvent ? e.key === 'Escape' : !ref.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', close);
    };
  }, [open]);

  // Keeps the column of buttons aligned on rows that have nothing extra to offer.
  if (items.length === 0) return <span className="w-8 shrink-0" aria-hidden />;

  return (
    <div ref={ref} className="relative shrink-0">
      <IconButton label={label} onClick={() => setOpen(!open)} expanded={open}>
        <MoreIcon />
      </IconButton>
      {open ? (
        <div
          role="menu"
          className="absolute end-0 top-full mt-1 z-20 min-w-[190px] py-1 rounded-md border border-border bg-surface shadow-lg"
        >
          {items.map((item) => (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              className={`block w-full text-start px-3 py-2 text-sm hover:bg-surface-alt ${
                item.danger ? 'text-danger' : ''
              } ${item.separated ? 'border-t border-border mt-1 pt-2.5' : ''}`}
              onClick={() => {
                setOpen(false);
                item.onSelect();
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function IconButton({
  label,
  onClick,
  expanded,
  children,
}: {
  label: string;
  onClick: () => void;
  expanded?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      aria-expanded={expanded}
      onClick={onClick}
      className="inline-flex items-center justify-center w-8 h-8 shrink-0 rounded text-muted hover:text-text hover:bg-surface-alt"
    >
      {children}
    </button>
  );
}

function ToggleRow({
  checked,
  onChange,
  label,
  hint,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  label: string;
  hint?: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium">{label}</div>
        {hint ? <div className="text-xs text-muted">{hint}</div> : null}
      </div>
      <Switch checked={checked} onChange={onChange} label={label} />
    </div>
  );
}

/**
 * The server's own sentence, under the form. ErrorNotice offers a page reload, which
 * inside a dialog would throw away everything just typed.
 */
function InlineError({ error }: { error: unknown }) {
  const { t } = useI18n();
  const message =
    error instanceof ApiError && error.code === 'NETWORK'
      ? t('common.networkError')
      : error instanceof Error
        ? error.message
        : t('common.unknownError');
  return (
    <p role="alert" className="text-sm text-danger">
      {message}
    </p>
  );
}

/** Colour first, for scanning a long checklist; the word is in the badge beside it. */
function StatusDot({ status }: { status: RegisterItem['status'] }) {
  const colour =
    status === 'valid'
      ? 'var(--color-success)'
      : status === 'expiring'
        ? 'var(--color-warning)'
        : status === 'expired' || status === 'missing'
          ? 'var(--color-danger)'
          : 'var(--color-border)';
  return <span aria-hidden className="w-2.5 h-2.5 shrink-0 rounded-full" style={{ background: colour }} />;
}

const iconProps = {
  viewBox: '0 0 20 20',
  'aria-hidden': true,
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

function FolderIcon({ open }: { open: boolean }) {
  return (
    <svg {...iconProps} className="w-5 h-5 shrink-0 text-[var(--color-warning)]">
      {open ? (
        <path d="M2.5 6V4.5a1 1 0 0 1 1-1h4l1.5 2h7a1 1 0 0 1 1 1V8M2.5 15.5 4.5 8h13l-2 7.5h-13Z" />
      ) : (
        <path d="M2.5 15.5v-11a1 1 0 0 1 1-1h4l1.5 2h7.5a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1h-13a1 1 0 0 1-1-1Z" />
      )}
    </svg>
  );
}

function FileIcon() {
  return (
    <svg {...iconProps} className="w-4 h-4 shrink-0 text-subtle">
      <path d="M11.5 2.5h-6a1 1 0 0 0-1 1v13a1 1 0 0 0 1 1h9a1 1 0 0 0 1-1v-10l-4-4Z" />
      <path d="M11.5 2.5v4h4" />
    </svg>
  );
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      {...iconProps}
      className={`w-4 h-4 shrink-0 text-subtle transition-transform rtl:-scale-x-100 ${open ? 'rotate-90 rtl:-rotate-90' : ''}`}
    >
      <path d="m7.5 4.5 5.5 5.5-5.5 5.5" />
    </svg>
  );
}

function EyeIcon() {
  return (
    <svg {...iconProps} className="w-4 h-4">
      <path d="M1.5 10S4.5 4.5 10 4.5 18.5 10 18.5 10 15.5 15.5 10 15.5 1.5 10 1.5 10Z" />
      <circle cx="10" cy="10" r="2.5" />
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg {...iconProps} className="w-4 h-4">
      <path d="M10 3.5v9M6.5 9 10 12.5 13.5 9M3.5 14.5v1a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1v-1" />
    </svg>
  );
}

function MoreIcon() {
  return (
    <svg viewBox="0 0 20 20" aria-hidden className="w-4 h-4" fill="currentColor">
      <circle cx="4.5" cy="10" r="1.5" />
      <circle cx="10" cy="10" r="1.5" />
      <circle cx="15.5" cy="10" r="1.5" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg {...iconProps} className="w-4 h-4">
      <path d="M10 4.5v11M4.5 10h11" />
    </svg>
  );
}
